import { ReactElement, useState, useEffect } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import { BASE_PATH } from '../../services/public-config';
import { track, SIGNUP_TYPE } from '../../services/analytics-client';
import { EVENTS } from '../../services/analytics';

import styles from './index.module.scss';
import { getOauthUrl } from '../../utils/oauth';
import { setRedirectCookie } from '../../utils/cookies';

type Props = {
  queryParams: string;
};

const HAS_ATTEMPTED_OAUTH_STORAGE_KEY = 'hasAttemptedOauth';

function SignupOptions({ queryParams }: Props): ReactElement {
  const router = useRouter();
  const [hasAttemptedOauth, setHasAttemptedOauth] = useState(false);
  const { redirect_to: redirectTo } = router.query;
  setRedirectCookie(redirectTo as string);

  useEffect(() => {
    try {
      setHasAttemptedOauth(!!window.sessionStorage.getItem(HAS_ATTEMPTED_OAUTH_STORAGE_KEY));
    } catch (e) {
      // it goes here if sessionStorage access is denied, and assume that they've attempted
      // oauth already just to be safe
      setHasAttemptedOauth(true);
    }
  }, []);

  return (
    <div className={styles['options-signup-container']}>
      <nav role="navigation">
        <ul className={styles['list-inline']}>
          <li>
            <a
              href={getOauthUrl(router, 'google')}
              title="Authenticate with Google"
              target={hasAttemptedOauth ? '_blank' : '_self'}
              onClick={() => {
                window.sessionStorage.setItem(HAS_ATTEMPTED_OAUTH_STORAGE_KEY, 'true');
                track(EVENTS.SELECT, { signuptype: SIGNUP_TYPE.GOOGLE });
              }}
              rel="noopener noreferrer"
            >
              <Image alt="Authenticate with Google" src={`${BASE_PATH}/google.svg`} width="17" height="17" />
              <span>Sign up with Google</span>
            </a>
          </li>
          <li>
            <a
              href={getOauthUrl(router, 'github')}
              title="Authenticate with Github"
              target={hasAttemptedOauth ? '_blank' : '_self'}
              onClick={() => {
                window.sessionStorage.setItem(HAS_ATTEMPTED_OAUTH_STORAGE_KEY, 'true');
                track(EVENTS.SELECT, { signuptype: SIGNUP_TYPE.GITHUB });
              }}
              rel="noopener noreferrer"
            >
              <Image alt="Authenticate with Github" src={`${BASE_PATH}/github.svg`} width="17" height="17" />
              <span>Sign up with GitHub</span>
            </a>
          </li>
          <li>
            <a
              id="signUpWithEmail"
              href={`register${queryParams}`}
              onClick={() => {
                track(EVENTS.SELECT, { signuptype: SIGNUP_TYPE.EMAIL });
              }}
              title="Authenticate with Email"
            >
              <Image alt="Authenticate with Email" src={`${BASE_PATH}/email.svg`} width="17" height="17" />
              <span>Sign up with email</span>
            </a>
          </li>
        </ul>
      </nav>
    </div>
  );
}

export default SignupOptions;
